import {
  DrawerForm,
  ProFormDateRangePicker,
  ProFormGroup,
  ProFormInstance,
  ProFormList,
  ProFormSelect,
  ProFormText,
  ProFormItem,
  ProFormTextArea,
  ProFormDigit,
  ProFormDependency,
} from '@ant-design/pro-components';
import { useIntl, getIntl } from '@umijs/max';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { UploadFile, UploadProps } from 'antd';
import { AutoComplete, Button, Divider, Form, Input, message, Modal, Popover, Space, Tooltip } from 'antd';
import {
  FilterOutlined,
  DownOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import 'braft-editor/dist/index.css';
import { TASK_BIG_TYPE_OPTIONS } from '@/constants/taskBigType';
import { getDictData, listUsers, uploadFile } from '@/services/common/api';
import { useModel, useRequest } from '@@/exports';
import {
  listSelectIssue,
  listIssueBasicInfo,
  addLabelTag,
  getMaxRangeDateByissueIds,
} from '@/services/rd/issues/api';
import { selectListV2 } from '@/services/settings/label';

import { FormInstance } from 'antd/lib';
import { RcFile } from 'antd/es/upload';
import { useEmotionCss } from '@ant-design/use-emotion-css';
import moment from 'moment';
import { listIssueByPlanIds, listPlanBySystemIds } from '@/services/rd/sprint/api';
import {
  createBranch,
  getTaskServiceByIssueIds,
  listGitByService,
  listServiceByServiceType,
  listTaskServiceByIssueIds,
} from '@/services/rd/task/api';
import TimeLineLog from '@/components/TimeLineLog';
import MyTinyMCEEditor from '@/components/MyTinyMCEEditor';
import HolidayCalendar from '@/pages/DashBoard/Duty/components/HolidayCalendar';
import { buildSystemSelectOptions } from '@/utils/systemSelectOptions';

export type FormValueType = {
  time: any;
} & Partial<API.TaskDetail>;

export type UpdateFormProps = {
  onCancel: (flag?: boolean, formVals?: FormValueType) => void;
  onSubmit: (values: FormValueType) => Promise<void>;
  updateModalOpen: boolean;
  isCreated: boolean;
  isStash: boolean;
  detail: any;
  complexRateTypeList: any;
  priorityLevelList: any;
  rdTaskType: any;
  rdTaskProgress: any;
  // serviceType: any;
  issueIds: any;
  planId: any;
  isDisabledPlan: boolean;
  isDisabledIssue: boolean;
  isBind: boolean;
  systemIdList: any;
  taskLabels: any;
  issueName: any;
  isIssueRequired: boolean;
};

const TaskModalForm: React.FC<UpdateFormProps> = (props) => {
  const intl = useIntl();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const { initialState } = useModel('@@initialState');
  const groupedSystemSelectOptions = useMemo(
    () => buildSystemSelectOptions(initialState?.systemList, { excludeAll: true }),
    [initialState?.systemList],
  );
  const rdTaskTypeOptions = useMemo(() => {
    const all = props.rdTaskType?.rd_task_type ?? [];
    return all
      .map((item: any) => ({
        label: item?.dictLable,
        value: item?.dictValue,
        remark: item?.remark,
      }))
      .filter((x: any) => x?.label && x?.value);
  }, [props.rdTaskType?.rd_task_type]);
  const getTaskBigTypeLabel = (value?: string) =>
    TASK_BIG_TYPE_OPTIONS.find((item: any) => item?.value === value)?.label || getIntl().formatMessage({ id: "筛选大类" });
  const getTaskBigTypeItemStyle = (active: boolean) => ({
    padding: '8px 12px',
    cursor: 'pointer',
    borderRadius: 6,
    fontSize: 12,
    color: active ? '#1677ff' : '#262626',
    backgroundColor: active ? '#e6f4ff' : 'transparent',
    fontWeight: active ? 600 : 400,
    transition: 'all 0.2s ease',
  });

  const taskRef = useRef<ProFormInstance>();
  const tagFormRef = React.useRef<FormInstance>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [state, setState] = useState('');
  const [showOthers, setShowOthers] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [disableDate, setDisableDate] = useState<any[]>([]);
  const [tagList, setTagList] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeRange, setTimeRange] = useState<any[]>([]); // 时间范围
  const [workDays, setWorkDays] = useState<number>(0); // 工作日
  const [formData, setFormData] = useState<any>(null); // 存储表单数据
  const { data: serviceTypeList, run: runServiceType } = useRequest(
    () => {
      return getDictData('service_type');
    },
    { manual: true },
  );

  const { data: userList, run: runUserList } = useRequest(
    () => {
      return listUsers();
    },
    { manual: true },
  );

  // 查需求下拉列表
  const { data: selectIssueList, run: runSelectIssueList } = useRequest(
    (param) => {
      if (param?.setlectType === 0) {
        return listSelectIssue(param);
      } else {
        return listIssueByPlanIds(param);
      }
    },
    { manual: true },
  );

  // 根据系统查规划下拉列表
  const { data: selectPlanList, run: runSelectPlanList } = useRequest(
    (param) => {
      return listPlanBySystemIds(param);
    },
    { manual: true },
  );
  // 根据系统查规划下拉列表
  const { data: gitList, run: runGitList } = useRequest(
    (serviceName) => {
      return listGitByService(serviceName);
    },
    { manual: true },
  );

  const { run: runMaxRangeDate } = useRequest(
    (ids) => {
      return getMaxRangeDateByissueIds(ids);
    },
    {
      manual: true,
      onSuccess: (dates) => {
        if (dates) {
          setDisableDate([moment(dates?.[0]), moment(dates?.[1])]);
        }
      },
    },
  );

  // 系统下拉列表
  // const { data: systemList, run: runSystemList } = useRequest(() => {
  //   return listChangeSystem()
  // }, { manual: false })

  // // 获取操作日志
  // const { data: opLog, run: runOpLog } = useRequest((opType) => {
  //   const param = {
  //     relationType: 'ISSUE',
  //     relationId: props.detail?.id,
  //     opType: opType
  //   }
  //   return listIssueOpLog(param)
  // }, { manual: true })

  // 添加标签
  const addTag = async (values: any) => {
    values.labelType = '2';
    const { resultCode, msg } = await addLabelTag(values) as any;
    if (resultCode === 'success') {
      const { resultObject } = await selectListV2('2');
      setTagList(resultObject);
      tagFormRef.current?.resetFields();
      message.success(getIntl().formatMessage({ id: "common.addedSuccessfully" }));
    } else {
      message.error(msg);
    }
  };

  // 系统变更时，刷新各个下拉数据
  const changeSystem = async (systemIds: any) => {
    taskRef.current?.setFieldValue('issueIdList', null);
    taskRef.current?.setFieldValue('planId', null);

    // 刷新需求下拉列表数据
    const param = {
      systemIdList: systemIds,
      issueIds: props?.isCreated ? null : props.detail?.issueIds,
      issueStatusType: props?.isCreated ? 1 : null,
      setlectType: 0,
    };
    runSelectIssueList(param);

    // 刷新规划下拉列表数据
    const planParam = {
      systemIds: systemIds,
    };
    runSelectPlanList(planParam);
  };

  // 系统变更时，刷新各个下拉数据
  const changePlan = (planId: any) => {
    taskRef.current?.setFieldValue('issueIdList', null);

    // 刷新需求下拉列表数据
    if (planId) {
      const param = {
        planIds: [planId],
        issueStatusType: props?.isCreated ? 1 : null,
        setlectType: 1,
      };
      runSelectIssueList(param);
    } else {
      const param = {
        systemIdList:
          taskRef.current?.getFieldValue('systemIdList')?.length > 0
            ? taskRef.current?.getFieldValue('systemIdList')
            : ['null'],
        issueIds: props?.isCreated ? null : props.detail?.issueIds,
        issueStatusType: props?.isCreated ? 1 : null,
        setlectType: 0,
      };
      runSelectIssueList(param);
    }
  };

  // const BraftEditorClass = useEmotionCss(({ token }) => {
  //   return {
  //     '.bf-content': {
  //       height: '320px'
  //     },
  //   }
  // })

  // const CommentClass = useEmotionCss(({ token }) => {
  //   return {
  //     '.ant-timeline-item-label': {
  //       width: '130px !important'
  //     },
  //     '.ant-timeline-item-tail': {
  //       insetInlineStart: '140px !important'
  //     },
  //     '.ant-timeline-item-head': {
  //       insetInlineStart: '140px !important'
  //     },
  //     '.ant-timeline-item-content': {
  //       insetInlineStart: '130px !important',
  //       width: 'calc(100% - 170px) !important'
  //     }
  //   }
  // })

  // const items: TabsProps['items'] = [
  //   {
  //     key: '',
  //     label: '全部记录',
  //     children: ''
  //   },
  //   {
  //     key: 'CHANGE_STATUS',
  //     label: '状态变更',
  //     children: ''
  //   },
  //   {
  //     key: 'COMMENT',
  //     label: '评论',
  //     children: ''
  //   },
  // ];

  const setFields = () => {
    taskRef.current?.setFieldsValue({
      taskName: props.detail?.taskName || '',
      issueIdList: props.detail?.issueIdList || [],
      taskType: props.detail?.taskType || '',
      taskLeader: props.detail?.taskLeader || '',
      time: [props.detail?.startDate, props.detail?.endDate],
      complexRate: props.detail?.complexRate?.toString() || '',
      priorityLevel: props.detail?.priorityLevel?.toString() || '',
      id: props.detail?.id || '',
      description: props.detail?.description || '',
      estimatedPersonDay: props.detail?.estimatedPersonDay || '',
      taskParticipants: props.detail?.taskParticipants || [],
      taskProgress: props.detail?.taskProgress.toString() || '',
      serviceParamList:
        props.detail?.serviceParamList?.map((item: any) => {
          return { ...item, isExitSelectBranch: true };
        }) || [],
      planId: props.detail?.planId || '',
      systemIdList: props.detail?.systemIdList || [],
      labelCodes: props?.detail?.labelCodes || [],
      remark: props?.detail?.remark,
    });
    setFileList(props.detail?.uploadAttachments);
    setTimeRange([props.detail?.startDate, props.detail?.endDate]);
    setState(props.detail?.description);
  };

  //确保在弹框打开时，detail数据已经更新
  useEffect(() => {
    if (props.updateModalOpen) {
      runUserList();
      setFormData(props.detail);
    } else {
      setFormData(null);
      setDisableDate([]);
      setTimeRange([]);
      setWorkDays(0);
    }
  }, [props?.updateModalOpen, props?.detail]);

  useEffect(() => {
    if (!props.updateModalOpen) {
      return;
    }
    setIsSubmitting(false);
    setTagList(props?.taskLabels);
    if (props.isCreated) {
      if (initialState?.defaultSystem?.systemDTO?.systemId !== 'all') {
        // 刷新规划下拉列表数据
        const planParam = {
          systemIds: [initialState?.defaultSystem?.systemDTO?.systemId],
        };
        runSelectPlanList(planParam);
        if (!props?.planId) {
          runSelectIssueList({
            name: '',
            issueIds: props.issueIds?.length > 0 ? props.issueIds : null,
            systemIdList:
              initialState?.defaultSystem?.systemDTO?.systemId !== 'all'
                ? [initialState?.defaultSystem?.systemDTO?.systemId]
                : null,
            issueStatusType: props?.isCreated && props.issueIds?.length <= 0 ? 1 : null,
            setlectType: 0,
          });
          if (props.issueIds?.length > 0) {
            runMaxRangeDate(props.issueIds);
          }
        } else {
          const param = {
            planIds: [props?.planId],
            issueStatusType: props?.isCreated ? 1 : null,
            setlectType: 1,
          };
          runSelectIssueList(param);
        }
      } else {
        // 刷新规划下拉列表数据
        const planParam = {
          systemIds: props?.systemIdList?.length > 0 ? props?.systemIdList : [null],
        };
        runSelectPlanList(planParam);

        if (!props?.planId) {
          runSelectIssueList({
            name: '',
            issueIds: props.issueIds?.length > 0 ? props.issueIds : null,
            systemIdList:
              initialState?.defaultSystem?.systemDTO?.systemId !== 'all'
                ? [initialState?.defaultSystem?.systemDTO?.systemId]
                : null,
            issueStatusType: props?.isCreated && props.issueIds?.length <= 0 ? 1 : null,
            setlectType: 0,
          });
          if (props.issueIds?.length > 0) {
            runMaxRangeDate(props.issueIds);
          }
        } else {
          const param = {
            planIds: [props?.planId],
            issueStatusType: props?.isCreated ? 1 : null,
            setlectType: 1,
          };
          runSelectIssueList(param);
        }
      }
      setFileList([]);
      setState('');
    }

    if (!props?.isCreated && formData) {
      setFields();
      runSelectIssueList({
        name: '',
        issueIds: props.detail?.issueIdList,
        systemIdList:
          taskRef.current?.getFieldValue('systemIdList')?.length > 0
            ? taskRef.current?.getFieldValue('systemIdList')
            : ['null'],
        issueStatusType: props?.isCreated ? 1 : null,
        setlectType: 0,
      });

      // 刷新规划下拉列表数据
      const planParam = {
        systemIds:
          taskRef.current?.getFieldValue('systemIdList')?.length > 0
            ? taskRef.current?.getFieldValue('systemIdList')
            : ['null'],
      };
      runSelectPlanList(planParam);
      if (props.detail?.issueIdList?.length > 0) {
        runMaxRangeDate(props.detail?.issueIdList);
      }
      // runOpLog('');
    }
    runServiceType();
  }, [formData, props?.updateModalOpen]);

  const onChange: UploadProps['onChange'] = async ({ file, fileList: newFileList }) => {
    if (!file.status) {
      newFileList.splice(fileList.indexOf(file), 1);
    }
    if (file.status === 'done') {
      let obj = {
        uid: newFileList[newFileList.length - 1].uid,
        name: file.response.fileName,
        status: newFileList[newFileList.length - 1].status,
        url: file.response.fileUrl,
        ...file.response,
      };
      newFileList[newFileList.length - 1] = obj;
    } else if (file.status === 'error') {
      message.error(getIntl().formatMessage({ id: "{value0} 文件上传失败" }, { value0: file.name }));
    }
    setFileList(newFileList);
  };
  const customRequest: UploadProps['customRequest'] = async ({ file, onSuccess, onError }) => {
    const fd = new FormData();
    fd.append('file', file);
    await uploadFile(fd)
      .then((resp) => {
        // 上传成功时，调用 onSuccess 并传递服务器返回的文件 URL
        onSuccess?.(resp?.resultObject);
      })
      .catch((error) => {
        // 上传失败时，调用 onError 并传递错误信息
        onError?.(error);
      });
  };

  const beforeUpload = (file: RcFile, fileList: any) => {
    const isLt2M = file.size / 1024 / 1024 < 5;
    if (!isLt2M) {
      // console.log(fileList.indexOf(file))
      message.error(getIntl().formatMessage({ id: "上传文件不能超过5MB" }));
      return false;
    }
    return true;
  };

  return (
    <DrawerForm
      formRef={taskRef}
      submitter={{ submitButtonProps: { loading: isSubmitting } }}
      title={
        <Space>
          {props.isCreated
            ? intl.formatMessage({
              id: 'pages.common.create',
              defaultMessage: getIntl().formatMessage({ id: "menu.rd.onlineBugCreate" }),
            })
            : intl.formatMessage({
              id: 'pages.common.edit',
              defaultMessage: getIntl().formatMessage({ id: "编辑" }),
            })}
          <span style={{ color: 'orange', fontSize: 12 }}>
            {getIntl().formatMessage({ id: "（注：任务时间不能超过需求起始至交付时间段）" })}
          </span>
        </Space>
      }
      width="880px"
      open={props.updateModalOpen}
      className={useEmotionCss(({ token }) => {
        return {
          '.ant-pro-form-list-action': {
            marginBlockEnd: 0,
          },
        };
      })}
      onFinish={async (value) => {
        if (isSubmitting) {
          return; // 如果正在提交，则阻止重复提交
        }
        if (value?.description?.length >= 65535) {
          message.error(getIntl().formatMessage({ id: "图片上传错误，请重试！" }));
          return;
        }
        setIsSubmitting(true);
        // if (commentContent) {
        //   coment();
        // }
        console.log(value);
        let flag = false;
        const countMap: Record<string, number> = {};
        value?.serviceParamList?.forEach((item: any) => {
          if (item?.gitBranch && !item?.isExitSelectBranch) {
            flag = true;
          }
          const identifier = `${item.serviceType}_${item.serviceName}`;
          if (countMap[identifier]) {
            countMap[identifier] += 1;
          } else {
            countMap[identifier] = 1;
          }
        });
        if (flag) {
          message.error(getIntl().formatMessage({ id: "存在未创建的分支，请点击分支后的“ + ”创建后再保存任务" }));
          setIsSubmitting(false);
          return;
        }
        const duplicates: string[] = [];
        value?.serviceParamList?.forEach((item: any) => {
          const identifier = `${item.serviceType}_${item.serviceName}`;
          if (countMap[identifier] > 1) {
            // 检查duplicates中是否已经包含了该项，避免重复添加
            const isAlreadyAdded = duplicates?.some(
              (duplicate) =>
                duplicate === item.serviceName,
            );
            if (!isAlreadyAdded) {
              duplicates.push(`${item.serviceName}`);
            }
          }
        });
        if (duplicates?.length > 0) {
          const uniqueList = [...new Set(duplicates)];
          message.error(getIntl().formatMessage({ id: "存在相同的服务，请自行Merge到一个分支后保存，服务分别是" }) + uniqueList);
          setIsSubmitting(false);
          return;
        }

        value.startDate = value?.time[0];
        value.endDate = value?.time[1];
        // value.description = myBrafrEditorRef?.current?.description;
        // value.issueIdList = value.issueIdList ? [value.issueIdList] : null
        props.onSubmit(value);
      }}
      onOpenChange={(open) => {
        if (!open) {
          props.onCancel();
        }
      }}
      drawerProps={{ destroyOnClose: true }}
    >
      <ProFormText
        // colProps={{ md: 12, xl: 8 }}
        name="taskName"
        label={intl.formatMessage({
          id: 'pages.task.name',
          defaultMessage: getIntl().formatMessage({ id: "任务名称" }),
        })}
        initialValue={props?.issueName}
        width={810}
        bordered={false}
        rules={[
          {
            required: true,
          },
        ]}
        fieldProps={{
          maxLength: 64, // 限制用户输入不超过64个字符
        }}
      />
      <ProFormGroup>
        <ProFormSelect
          mode={'multiple'}
          disabled={(props.isDisabledIssue || props.isDisabledPlan) && props.isBind}
          showSearch={true}
          name="systemIdList"
          width={390}
          tooltip={getIntl().formatMessage({ id: "系统与Sprint、需求下拉框会有联动效果" })}
          label={intl.formatMessage({
            id: 'pages.system.title',
            defaultMessage: getIntl().formatMessage({ id: "系统" }),
          })}
          rules={[
            {
              required: true,
              message: getIntl().formatMessage({ id: "请选择系统" }),
            },
          ]}
          onChange={(row) => changeSystem(row)}
          initialValue={
            initialState?.defaultSystem?.systemDTO?.systemId === 'all'
              ? props?.systemIdList
              : props.isDisabledPlan
                ? props?.systemIdList
                : [initialState?.defaultSystem?.systemDTO?.systemId]
          }
          options={groupedSystemSelectOptions}
        />
        <ProFormItem
          label={intl.formatMessage({
            id: 'pages.task.type',
            defaultMessage: getIntl().formatMessage({ id: "任务类型" }),
          })}
          required
        >
          <ProFormDependency name={['taskBigType']}>
            {({ taskBigType }) => (
              <>
                <ProFormText name="taskBigType" hidden />
                <Space size={8}>
                  <Popover
                    trigger="hover"
                    placement="bottomLeft"
                    overlayInnerStyle={{ padding: 8, borderRadius: 10 }}
                    content={
                      <div style={{ width: 180 }}>
                        <div
                          style={getTaskBigTypeItemStyle(!taskBigType)}
                          onClick={() => {
                            taskRef.current?.setFieldValue('taskBigType', undefined);
                            taskRef.current?.setFieldValue('taskType', undefined);
                          }}
                        >
                          {getIntl().formatMessage({ id: "common.all" })}
                        </div>
                        {TASK_BIG_TYPE_OPTIONS.map((item: any) => (
                          <div
                            key={item.value}
                            style={getTaskBigTypeItemStyle(taskBigType === item.value)}
                            onClick={() => {
                              taskRef.current?.setFieldValue('taskBigType', item.value);
                              taskRef.current?.setFieldValue('taskType', undefined);
                            }}
                          >
                            {item.label}
                          </div>
                        ))}
                      </div>
                    }
                  >
                    <div
                      style={{
                        minWidth: 88,
                        maxWidth: 140,
                        height: 28,
                        padding: '0 10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: '1px solid #f0f0f0',
                        borderRadius: 14,
                        background: taskBigType ? '#f6ffed' : '#fafafa',
                        color: taskBigType ? '#389e0d' : '#8c8c8c',
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      <FilterOutlined style={{ fontSize: 12, marginRight: 6 }} />
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          marginRight: 8,
                        }}
                      >
                        {taskBigType ? getTaskBigTypeLabel(taskBigType) : getIntl().formatMessage({ id: "筛选大类" })}
                      </span>
                      <DownOutlined style={{ fontSize: 12, color: '#bfbfbf' }} />
                    </div>
                  </Popover>
                  <ProFormSelect
                    name="taskType"
                    showSearch
                    width={262}
                    placeholder={getIntl().formatMessage({ id: "任务类型" })}
                    rules={[{ required: true }]}
                    options={rdTaskTypeOptions
                      .filter((x: any) =>
                        taskBigType
                          ? String(x?.remark ?? '').trim() === String(taskBigType)
                          : true,
                      )
                      .map((x: any) => ({ label: x.label, value: x.value }))}
                  />
                </Space>
              </>
            )}
          </ProFormDependency>
        </ProFormItem>
      </ProFormGroup>
      <ProFormGroup>
        <ProFormSelect
          disabled={props.isDisabledPlan && props.isBind}
          showSearch={true}
          width={390}
          name="planId"
          label={
            <Space>
              {intl.formatMessage({
                id: 'pages.task.sprint',
                defaultMessage: 'Sprint',
              })}
              <Tooltip title={<div>{getIntl().formatMessage({ id: "Sprint下拉选择只有筛选需求的作用，任务只会与需求直接关联" })}</div>}>
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          }
          onChange={(row) => changePlan(row)}
          initialValue={props.planId ? props.planId : ''}
          options={selectPlanList?.map((item: any) => ({
            label: item.planName,
            value: item.id,
          }))}
        />

        <ProFormSelect
          disabled={props.isDisabledIssue && props.isBind}
          showSearch={true}
          // colProps={{ md: 12, xl: 8 }}
          name="issueIdList"
          mode={'multiple'}
          width={390}
          rules={[{ required: props.isIssueRequired }]}
          label={
            intl.formatMessage({
              id: 'pages.task.issues',
              defaultMessage: getIntl().formatMessage({ id: "pages.issueReview.form.issues" }),
            }) + getIntl().formatMessage({ id: "（选择服务分支必须选择需求）" })
          }
          initialValue={props.issueIds}
          options={selectIssueList?.results?.map((item: any) => ({
            label: item.issueName,
            value: item.id,
          }))}
          onChange={async (...id) => {
            if (!taskRef.current?.getFieldValue('taskName')) {
              taskRef?.current?.setFieldValue('taskName', id[1][0]?.title);
            }

            // 清空时间相关字段
            taskRef.current?.setFieldValue('time', undefined);
            setTimeRange([]);
            setWorkDays(0);

            if (id[0] && Array.isArray(id[0]) && id[0]?.length > 0) {
              runMaxRangeDate(id[0]);
            } else {
              setDisableDate([]);
            }

            const { resultObject } = await listTaskServiceByIssueIds(id[0]);
            let listNew: any[] = [];
            if (resultObject) {
              let sList = resultObject?.map((item) => {
                return { ...item, isExitSelectBranch: true };
              });
              listNew = sList;
            }
            // else {
            //   let sList = [{
            //     serviceType: '',
            //     serviceName: '',
            //     gitBranch: '',
            //     isExitBranch: false
            //   }]
            //   listNew = sList
            // }
            taskRef?.current?.setFieldValue('serviceParamList', listNew);
          }}
          fieldProps={{
            onSearch: (name) => {
              if (!name) {
                return;
              }
              runSelectIssueList({
                name: name,
                issueIds: props.detail?.issueIdList ? props.detail?.issueIdList : null,
                systemIdList:
                  taskRef.current?.getFieldValue('systemIdList').length > 0
                    ? taskRef.current?.getFieldValue('systemIdList')
                    : null,
                planIds: props?.planId ? [props?.planId] : [],
                issueStatusType: props?.isCreated ? 1 : null,
                setlectType: props?.planId ? 1 : 0,
              });
            },
          }}
        />
      </ProFormGroup>

      <ProFormGroup>
        <ProFormSelect
          colProps={{ md: 12, xl: 8 }}
          showSearch={true}
          name="taskLeader"
          disabled={!props.isCreated}
          width={390}
          label={intl.formatMessage({
            id: 'pages.task.owner',
            defaultMessage: getIntl().formatMessage({ id: "主负责人" }),
          })}
          rules={[
            {
              required: true,
            },
          ]}
          initialValue={initialState?.currentUser?.userName}
          options={userList?.map((item: any) => ({
            label: item.userName,
            value: item.userCode,
          }))}
        />

        {/* <ProFormSelect
          showSearch={true}
          mode={'multiple'}
          name="taskParticipants"
          width={390}
          initialValue={[initialState?.currentUser?.userName]}
          tooltip={'任务参与相关人员'}
          label={
            intl.formatMessage({
              id: 'pages.task.taskParticipants',
              defaultMessage: '参与人',
            })
          }
          rules={[
            {
              required: true,
            },
          ]}
          options={userList?.map((item: any) => ({
            label: item.userName,
            value: item.userCode
          }))} /> */}
        <ProFormSelect
          showSearch={true}
          mode={'multiple'}
          name="labelCodes"
          width={390}
          tooltip={getIntl().formatMessage({ id: "该下拉列表展示的是“任务中心”标签。" })}
          label={intl.formatMessage({
            id: 'pages.common.tag',
            defaultMessage: getIntl().formatMessage({ id: "common.label" }),
          })}
          fieldProps={{
            dropdownRender: (menu) => (
              <>
                {menu}
                <Divider style={{ margin: '8px 0' }} />
                <Form style={{ height: 40 }} onFinish={addTag} ref={tagFormRef}>
                  <Space style={{ padding: '0 4px 4px' }}>
                    <Form.Item label={''} name={'labelNameCn'} rules={[{ required: true }]}>
                      <Input placeholder={getIntl().formatMessage({ id: "中文名" })} />
                    </Form.Item>
                    <Form.Item label={''} name={'labelNameEn'} rules={[{ required: true }]}>
                      <Input placeholder={getIntl().formatMessage({ id: "英文名" })} />
                    </Form.Item>
                    <Form.Item>
                      <Button type="text" size={'small'} htmlType="submit">
                        {getIntl().formatMessage({ id: "添加标签" })}
                      </Button>
                    </Form.Item>
                  </Space>
                </Form>
              </>
            ),
          }}
          options={tagList?.map((item) => ({ label: item.labelName, value: item.labelCode }))}
        />
        <ProFormItem
          name="time"
          label={intl.formatMessage({
            id: 'pages.task.form.time',
            defaultMessage: getIntl().formatMessage({ id: "pages.sprint.form.time" }),
          })}
          rules={[
            {
              required: true,
              validator: (_, value) => {
                if (!value || value.length !== 2 || !value[0] || !value[1]) {
                  return Promise.reject(getIntl().formatMessage({ id: "时间不能为空" }));
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <HolidayCalendar
            style={{ width: 390 }}
            defaultValue={[
              timeRange?.[0] ? moment(timeRange[0]) : undefined,
              timeRange?.[1] ? moment(timeRange[1]) : undefined,
            ]}
            onChange={(dates, workDays) => {
              if (dates) {
                setTimeRange([
                  moment(dates[0]).format('YYYY-MM-DD'),
                  moment(dates[1]).format('YYYY-MM-DD'),
                ]);
              }
              setWorkDays(workDays ?? 0);
            }}
            disabledDate={(current) => {
              return (
                disableDate[0] &&
                disableDate[1] &&
                (current.isBefore(disableDate[0], 'day') || current.isAfter(disableDate[1], 'day'))
              );
            }}
          />
        </ProFormItem>
        {/* <ProFormDateRangePicker
          name="time"
          width={390}
          label={intl.formatMessage({
            id: 'pages.task.form.time',
            defaultMessage: '开始-结束',
          })}
          fieldProps={{
            disabledDate: (current) => {
              return (
                disableDate[0] &&
                disableDate[1] &&
                (current.isBefore(disableDate[0], 'day') || current.isAfter(disableDate[1], 'day'))
              );
            },
          }}
          rules={[
            {
              required: true,
              validator: (_, value) => {
                // console.log(value)
                if (!value || value.length !== 2 || !value[0] || !value[1]) {
                  return Promise.reject('时间不能为空');
                }

                // if (moment(value).endOf('day').isBefore(moment().startOf('day'))) {
                //   return Promise.reject('时间不能小于今天');
                // }
                if (value && value.length === 2) {
                  const [start, end] = value;
                  const dayDifference = moment(end).diff(moment(start), 'days');

                  // 检查是否超过3天
                  if (dayDifference > 2) {
                    return Promise.reject(new Error('时间跨度不能超过3天，请拆解'));
                  }

                  // // 检查是否跨周
                  // const startWeek = start.week();
                  // const endWeek = end.week();
                  // if (startWeek !== endWeek) {
                  //   return Promise.reject(new Error('所选日期范围不能跨周'));
                  // }
                }
                return Promise.resolve();
              },
            },
          ]}
        /> */}
        <ProFormDigit
          name="estimatedPersonDay"
          tooltip={
            <div>
              {getIntl().formatMessage({ id: "参与总人数 * 工时（天）" })}
              <br />
              {getIntl().formatMessage({ id: "成本预估=人/天*3000，可直观告知业务方预估花费成本，需对方考虑ROI是否合理。" })}
              <br />
              {getIntl().formatMessage({ id: "以及后期如果成本过高，需要上升到不同维度进行决策。" })}
            </div>
          }
          label={intl.formatMessage({
            id: 'pages.task.estimatedPersonDay',
            defaultMessage: getIntl().formatMessage({ id: "预估人天" }),
          })}
          width={390}
          required={true}
          rules={[
            {
              required: true,
              validator: (_, value) => {
                if (!value) {
                  return Promise.reject(getIntl().formatMessage({ id: "预估人天不能为空" }));
                }
                if (value < 0) {
                  return Promise.reject(getIntl().formatMessage({ id: "预估人天不能小于0" }));
                }
                if (value > 3) {
                  return Promise.reject(getIntl().formatMessage({ id: "预估人天不能超过3天，请拆解" }));
                }
                return Promise.resolve();
              },
            },
          ]}
        />
      </ProFormGroup>

      {/* <a
        key="others"
        onClick={() => {
          if (showOthers) {
            setShowOthers(false)
          } else {
            setShowOthers(true)
          }
        }}
      >
        展开更多录入项 {showOthers ? <UpOutlined /> : <DownOutlined />}
      </a>
      {showOthers && <> */}
      <ProFormGroup>
        <ProFormSelect
          showSearch={true}
          colProps={{ md: 12, xl: 8 }}
          name="priorityLevel"
          width={390}
          initialValue={'3'}
          label={intl.formatMessage({
            id: 'pages.task.priorityLevel',
            defaultMessage: getIntl().formatMessage({ id: "优先级" }),
          })}
          // rules={[{ required: true, }]}
          options={props.priorityLevelList?.priority_level?.map((item: any) => ({
            label: item.dictLable,
            value: item.dictValue,
          }))}
        />
        <ProFormSelect
          showSearch={true}
          name="complexRate"
          width={390}
          tooltip={
            <div>
              <div>{getIntl().formatMessage({ id: "简单 - 小于5个工作日" })}</div>
              <div>{getIntl().formatMessage({ id: "中等 - 5~10个工作日" })}</div>
              <div>{getIntl().formatMessage({ id: "复杂 - 大于10个工作日" })}</div>
            </div>
          }
          label={intl.formatMessage({
            id: 'pages.task.complexRate',
            defaultMessage: getIntl().formatMessage({ id: "pages.issuePool.complexRate" }),
          })}
          rules={[
            {
              // required: true,
            },
          ]}
          options={props.complexRateTypeList?.complex_rate_type?.map((item: any) => ({
            label: item.dictLable,
            value: item.dictValue,
          }))}
        />
      </ProFormGroup>
      <ProFormGroup>
        <ProFormTextArea
          name="remark"
          label={getIntl().formatMessage({ id: "common.remark" })}
          width={390}
          fieldProps={{ autoSize: { minRows: 1, maxRows: 5 }, maxLength: 1000, showCount: true }}
        />
      </ProFormGroup>
      {taskRef?.current?.getFieldValue('issueIdList')?.length > 0 && (
        <ProFormList
          name="serviceParamList"
          creatorButtonProps={{
            creatorButtonText: getIntl().formatMessage({ id: "添加一行服务分支" }),
            type: 'primary',
          }}
          copyIconProps={false}
          deleteIconProps={false}
        // initialValue={[
        //   {
        //     serviceName: '',
        //     gitBranch: '',
        //     isExitBranch: false,
        //     isExitSelectBranch: false,
        //     isDropdownOpen: false
        //   },
        // ]}
        // actionRender={(field, action) =>
        //   [,
        //   ]}
        >
          {(meta, index, action, count) => {
            const row = action.getCurrentRowData();
            // console.log(row)
            return (
              <ProFormGroup key="group">
                <ProFormText
                  label={getIntl().formatMessage({ id: "pages.common.id" })}
                  allowClear={false}
                  disabled
                  width={38}
                  fieldProps={{ value: `${index + 1}` }}
                />
                <ProFormSelect
                  showSearch={true}
                  name="serviceType"
                  width={100}
                  tooltip={getIntl().formatMessage({ id: "该任务相关联的服务的服务类型" })}
                  label={intl.formatMessage({
                    id: 'pages.task.serviceType',
                    defaultMessage: getIntl().formatMessage({ id: "common.serviceType" }),
                  })}
                  onChange={(value) => {
                    const i = action.getCurrentRowData();
                    i.serviceName = '';
                    i.gitBranch = '';
                    i.createUserName = '';
                    action.setCurrentRowData(i);
                  }}
                  rules={[{ required: true }]}
                  options={serviceTypeList?.service_type?.map((item: any) => ({
                    label: item.dictLable,
                    value: item.dictValue,
                  }))}
                />

                <ProFormSelect
                  showSearch={true}
                  name="serviceName"
                  width={185}
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                  tooltip={
                    getIntl().formatMessage({ id: "该任务相关联的服务(注:服务类型选择后，服务名称下拉框会自动加载服务名称)" })
                  }
                  label={intl.formatMessage({
                    id: 'pages.task.service',
                    defaultMessage: getIntl().formatMessage({ id: "pages.task.service" }),
                  })}
                  onChange={async (value) => {
                    const i = action.getCurrentRowData();
                    // const param = {
                    //   issueIds: taskRef?.current?.getFieldValue('issueIdList'),
                    //   serviceParam: i,
                    // }
                    // const { resultObject } = await getTaskServiceByIssueIds(param);
                    // if (resultObject) {
                    //   i.createUserName = resultObject?.createUserName
                    //   i.gitBranch = resultObject?.gitBranch;
                    //   i.isExitBranch = true;
                    // } else {
                    i.isExitBranch = false;
                    i.gitBranch = '';
                    i.createUserName = '';
                    runGitList(value);
                    // }
                    // i.isDropdownOpen = false
                    action.setCurrentRowData(i);
                  }}
                  dependencies={['serviceType']}
                  request={async (params) => {
                    if (params?.serviceType) {
                      const { resultObject } = await listServiceByServiceType(params?.serviceType);
                      return resultObject?.map((item: any) => ({
                        label: item.serviceName,
                        value: item.serviceName,
                      }));
                    }
                  }}
                />
                <ProFormItem
                  label={getIntl().formatMessage({ id: "输入分支“+”创建或选择分支" })}
                  name="gitBranch"
                  rules={[
                    // { required: true, message: '请输入或选择分支' },
                    {
                      pattern: /^[a-zA-Z0-9._/-]+$/,
                      message: getIntl().formatMessage({ id: "数字、字母、小数点、短横线、下划线" }),
                    },
                  ]}
                >
                  <AutoComplete
                    style={{ width: 205 }}
                    // open={action.getCurrentRowData()?.isDropdownOpen}
                    allowClear
                    // onSearch={handleSearch}
                    placeholder={getIntl().formatMessage({ id: "输入要创建的分支名称或选择分支" })}
                    options={gitList?.map((item: any) => ({
                      label: item.branchName,
                      value: item.branchName,
                    }))}
                    filterOption={(inputValue, option) =>
                      String(option!.value).toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                    // disabled={action.getCurrentRowData()?.isExitBranch}
                    onDropdownVisibleChange={(open) => {
                      if (open) {
                        const i = action.getCurrentRowData();
                        runGitList(i?.serviceName);
                      }
                    }}
                    // onBlur={(s) => {
                    //   const i = action.getCurrentRowData();
                    //   i.isDropdownOpen = i.isDropdownOpen ? false : false
                    //   action.setCurrentRowData(i);
                    // }}
                    onChange={(value) => {
                      const branch = gitList?.filter(function (val: any) {
                        return value === val.branchName;
                      });
                      const i = action.getCurrentRowData();
                      if (!branch || branch?.length <= 0) {
                        i.isExitSelectBranch = false;
                      } else {
                        i.isExitSelectBranch = true;
                      }
                      i.createUserName = branch?.[0]?.createBy || '';
                      // i.isDropdownOpen = true
                      action.setCurrentRowData(i);
                    }}
                  />
                </ProFormItem>
                <ProFormItem label={getIntl().formatMessage({ id: "操作" })}>
                  <>
                    <a
                      style={{ marginRight: 5 }}
                      hidden={row.isExitSelectBranch || !row?.gitBranch}
                      onClick={async () => {
                        if (!row || !row?.serviceName || !row?.gitBranch) {
                          message.error(getIntl().formatMessage({ id: "服务类型、服务、分支不能为空！" }));
                          return;
                        }
                        const { status } = await createBranch(row);
                        if (status === 'success') {
                          row.isExitSelectBranch = true;
                          row.isDropdownOpen = false;
                          row.createUserName = initialState?.currentUser?.nickName;
                          action.setCurrentRowData(row);
                        }
                      }}
                    >
                      <Tooltip title={getIntl().formatMessage({ id: "创建远程分支" })}>
                        <PlusOutlined />
                      </Tooltip>
                    </a>
                    <a onClick={() => action.remove(index)}>
                      <Tooltip title={getIntl().formatMessage({ id: "删除当前行" })}>
                        <DeleteOutlined style={{ color: 'red', marginRight: 5 }} />
                      </Tooltip>
                    </a>
                    {/* <a hidden={row?.isExitBranch}
                        onClick={() => {
                          const list = taskRef?.current?.getFieldValue('serviceParamList')
                          const item = list[action.getCurrentRowData().name];
                          item.isDropdownOpen = item.isDropdownOpen ? false : true
                          taskRef?.current?.setFieldValue('serviceParamList', list)
                        }}>
                        <Tooltip title={taskRef?.current?.getFieldValue('serviceParamList')[action.getCurrentRowData().name]?.isDropdownOpen ? '关闭分支下拉框' : '展开分支下拉框'}><SelectOutlined style={{ color: '#3179F7' }} /></Tooltip></a> */}

                    <span hidden={!row?.createUserName}> Create By {row?.createUserName}</span>
                  </>
                </ProFormItem>
              </ProFormGroup>
            );
          }}
        </ProFormList>
      )}
      {/* </>} */}
      {/* <ProFormGroup>
        <ProFormUploadDragger width={700} name='uploadAttachments' label='上传附件' title={false} description={false}
          fieldProps={{
            customRequest: customRequest,
            fileList: fileList,
            beforeUpload: beforeUpload,
            onChange: onChange,
            onPreview: (file) => {
              setPreviewUrl(file.url || file.thumbUrl);
              setPreviewVisible(true);
            }
          }
          } />
      </ProFormGroup> */}
      <ProFormItem label={getIntl().formatMessage({ id: "描述" })} name="description">
        <MyTinyMCEEditor
          data={state}
          readOnly={false}
          onEdit={() => { }}
          onChange={(editorState) => {
            taskRef?.current?.setFieldsValue({ description: editorState });
          }}
          height={200}
          extendControls={undefined}
        />
      </ProFormItem>
      {!props.isCreated && (
        <ProFormItem label={''} name="others">
          <TimeLineLog
            id={props.detail?.id}
            relationType={'RD_TASK'}
            commentReadOnly={true}
            showComment={false}
          />
        </ProFormItem>
      )}

      <Modal
        width={'80%'}
        open={previewVisible}
        footer={null}
        onCancel={() => {
          setPreviewVisible(false);
        }}
      >
        <iframe
          title={getIntl().formatMessage({ id: "链接内容" })}
          src={previewUrl}
          style={{ width: '100%', height: document.body.offsetHeight - 200, border: 'none' }}
        />
      </Modal>
    </DrawerForm>
  );
};

export default TaskModalForm;
